'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { IconButton, Paper, Stack, InputAdornment, FormHelperText, Snackbar, Alert } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import ViewContainer from '../layouts/ViewContainer';
import { useTelegram } from '@/providers/TelegramProvider';
import PaymentStatusView from './PaymentStatusView';
import { isPromoActive, calculateBonus, getPromoEndDate } from '@/utils/promo';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Добавляем интерфейс для пропсов компонента PaymentMethodCard
interface PaymentMethodCardProps {
    method: string;
    label: string;
    icon: React.ReactNode;
    isActive: boolean;
    onClick: () => void;
    disabled?: boolean;
}

interface TopUpViewProps {
    onBack: () => void;
}

export default function TopUpView({ onBack }: TopUpViewProps) {
    const { user, getServerTime } = useTelegram();
    const [amount, setAmount] = React.useState('150');
    const [paymentMethod, setPaymentMethod] = React.useState<'sbp' | 'card'>('sbp'); // Типизируем метод оплаты
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error' | 'info'>('info');
    const [loading, setLoading] = React.useState(false);
    const [paymentId, setPaymentId] = React.useState<string | null>(null);
    const [paymentUrl, setPaymentUrl] = React.useState<string | null>(null); // Добавляем состояние для URL оплаты

    const [timeLeft, setTimeLeft] = React.useState<string>('');
    const [promoActive, setPromoActive] = React.useState(false);

    React.useEffect(() => {
        if (!user) return;

        const userCreatedAt = user.created ? new Date(user.created) : undefined;
        const serverNow = getServerTime().getTime();
        
        const active = isPromoActive(userCreatedAt, serverNow);
        setPromoActive(active);

        if (active) {
            const endDate = getPromoEndDate(userCreatedAt, serverNow);
            if (!endDate) return;

            // Первоначальный расчет оставшегося времени (в миллисекундах)
            let remainingTime = endDate - serverNow;

            const updateTimer = () => {
                if (remainingTime < 0) {
                    setPromoActive(false);
                    return false; // Остановить таймер
                }

                // Ограничиваем максимальное время 7 днями
                const maxDistance = 7 * 24 * 60 * 60 * 1000;
                const displayTime = Math.min(remainingTime, maxDistance);

                const days = Math.floor(displayTime / (1000 * 60 * 60 * 24));
                const hours = Math.floor((displayTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((displayTime % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((displayTime % (1000 * 60)) / 1000);


                setTimeLeft(`${days}д ${hours}ч ${minutes}м`);
                
                // Отнимаем одну секунду для следующего тика
                remainingTime -= 1000;
                
                return true; // Продолжать таймер
            };

            // Вызываем сразу, чтобы не было задержки в 1 секунду
            updateTimer();
            const interval = setInterval(() => {
                if (!updateTimer()) {
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [user, getServerTime]);

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (/^\d*$/.test(value)) {
            setAmount(value);
        }
    };

    // Функция для открытия ссылки в браузере (не внутри Web App)
    const openPaymentLink = (url: string) => {
        try {
            // Создаем временную ссылку и эмулируем клик для открытия в браузере
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank'; // Открываем в новой вкладке
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error opening link:', error);
            // Запасной вариант
            window.open(url, '_blank');
        }
    };

    const handleTopUp = async () => {
        const amountNum = Number(amount);

        if (amountNum < 150) {
            setSnackbarSeverity('error');
            setSnackbarMessage(`Минимальная сумма пополнения: 150₽`);
            setSnackbarOpen(true);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('/payment/yookassa/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: amountNum,
                    method: paymentMethod === 'card' ? 'bank_card' : 'sbp'
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.confirmationUrl) {
                // Открываем ссылку в браузере (не внутри Web App)
                openPaymentLink(data.confirmationUrl);

                if (data.paymentId) {
                    setPaymentId(data.paymentId);
                    setPaymentUrl(data.confirmationUrl);

                    setSnackbarSeverity('info');
                    setSnackbarMessage('Страница оплаты открыта в браузере. Если не открылась, используйте кнопку ниже');
                    setSnackbarOpen(true);
                } else {
                    setSnackbarSeverity('error');
                    setSnackbarMessage('Платеж создан, но не удалось получить ID для отслеживания');
                    setSnackbarOpen(true);
                }
            } else {
                setSnackbarSeverity('error');
                setSnackbarMessage(data.error || 'Ошибка создания платежа');
                setSnackbarOpen(true);
            }
        } catch (e) {
            console.error('Payment error:', e);
            setSnackbarSeverity('error');
            setSnackbarMessage('Ошибка сети. Пожалуйста, попробуйте позже');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const isAmountInvalid = Number(amount) > 0 && Number(amount) < 150;
    
    const userCreatedAt = user?.created ? new Date(user.created) : undefined;
    const bonusAmount = promoActive ? calculateBonus(Number(amount), userCreatedAt, getServerTime().getTime()) : 0;

    // Выносим PaymentMethodCard в отдельный компонент внутри основного
    const PaymentMethodCard = ({ method, label, icon, isActive, onClick, disabled }: PaymentMethodCardProps) => (
        <Paper
            onClick={!disabled ? onClick : undefined}
            elevation={0}
            sx={{
                p: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                cursor: disabled ? 'default' : 'pointer',
                borderRadius: 1,
                transition: 'all 0.2s',
                background: isActive
                    ? "rgba(255, 94, 0, 0.15)"
                    : "rgba(0, 0, 0, 0.3)",
                border: '1px solid',
                borderColor: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
                opacity: disabled ? 0.5 : 1,
                '&:hover': disabled ? {} : {
                    borderColor: 'primary.light',
                    background: isActive
                        ? "rgba(255, 94, 0, 0.25)"
                        : "rgba(0, 0, 0, 0.4)",
                }
            }}
        >
            {icon}
            <Typography
                variant="body2"
                fontWeight="bold"
                color={isActive ? 'primary.main' : 'text.secondary'}
            >
                {label}
            </Typography>
        </Paper>
    );

    // Если есть paymentId, показываем статус оплаты с URL для резервного открытия
    if (paymentId && paymentUrl) {
        return (
            <PaymentStatusView
                paymentId={paymentId}
                onBack={onBack}
                paymentUrl={paymentUrl} // Передаем URL для резервных кнопок
            />
        );
    }

    return (
        <>
            <ViewContainer title="Пополнение" onClose={onBack}>
                <Stack spacing={4}>
                    {/* Promo Banner */}
                    {promoActive && (
                        <Paper sx={{
                            p: 1.5,
                            borderRadius: 1,
                            background: "linear-gradient(145deg, rgba(220, 38, 38, 0.15) 0%, rgba(220, 38, 38, 0.05) 100%)",
                            border: '1px solid',
                            borderColor: 'error.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                        }}>
                            <Typography variant="body2" fontWeight="bold" color="error.light">
                                Акция +30%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                •
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                <AccessTimeIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption" fontWeight="bold">{timeLeft}</Typography>
                            </Box>
                        </Paper>
                    )}

                    {/* Amount Input */}
                    <Box>
                        <TextField
                            variant="standard"
                            fullWidth
                            value={amount}
                            onChange={handleAmountChange}
                            error={isAmountInvalid}
                            disabled={loading}
                            slotProps={{
                                htmlInput: {
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    autoComplete: 'off',
                                },
                                input: {
                                    disableUnderline: true,
                                    sx: {
                                        fontSize: '2.5rem',
                                        fontWeight: 'bold',
                                        color: 'text.primary',
                                        borderBottom: '2px solid',
                                        borderColor: 'divider',
                                        pb: 1,
                                        transition: (theme) => theme.transitions.create('border-color'),
                                        '&.Mui-focused': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography variant="h4" component="span" fontWeight="bold" color="text.secondary">
                                                ₽
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <FormHelperText sx={{
                                fontWeight: 'bold',
                                color: isAmountInvalid ? 'error.light' : 'text.secondary',
                                m: 0
                            }}>
                                {isAmountInvalid ? `Минимум 150₽` : 'Введите сумму'}
                            </FormHelperText>

                            {promoActive && bonusAmount > 0 && (
                                <Typography variant="caption" color="success.light" fontWeight="bold">
                                    +{bonusAmount} ₽ бонусом
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Payment Methods */}
                    <Box>
                        <Stack direction="row" spacing={2}>
                            <PaymentMethodCard
                                method="sbp"
                                label="СБП"
                                isActive={paymentMethod === 'sbp'}
                                onClick={() => setPaymentMethod('sbp')}
                                disabled={loading}
                                icon={
                                    <Box
                                        sx={{
                                            height: 40,
                                            width: 80,
                                            bgcolor: paymentMethod === 'sbp' ? 'primary.main' : 'text.secondary',
                                            // Убедитесь, что файл /sbp.svg существует
                                            mask: 'url(/sbp.svg) no-repeat center / contain',
                                            WebkitMask: 'url(/sbp.svg) no-repeat center / contain',
                                            transition: 'background-color 0.2s',
                                        }}
                                    />
                                }
                            />
                            <PaymentMethodCard
                                method="card"
                                label="Карта"
                                isActive={paymentMethod === 'card'}
                                onClick={() => setPaymentMethod('card')}
                                disabled={loading}
                                icon={
                                    <CreditCardIcon
                                        sx={{
                                            fontSize: 40,
                                            color: paymentMethod === 'card' ? 'primary.main' : 'text.secondary',
                                            transition: 'color 0.2s',
                                        }}
                                    />
                                }
                            />
                        </Stack>
                    </Box>

                    {/* Footer Button */}
                    <Button
                        onClick={handleTopUp}
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={loading}
                        sx={{
                            fontWeight: 'bold',
                            py: 1.5,
                            fontSize: '1rem',
                            textTransform: 'none',
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Пополнить баланс'}
                    </Button>
                </Stack>
            </ViewContainer>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                    action={
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={handleSnackbarClose}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}